<template>
  <Transition
    enter-active-class="transition-opacity duration-200 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-150 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="modelValue" class="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" @click.self="handleBackdropClick">
      <!-- Modal content wrapper -->
      <div class="relative z-10 flex h-full items-center justify-center p-4" @click.self="handleBackdropClick">
        <div
          class="max-h-[90vh] w-full overflow-y-auto rounded-2xl shadow-2xl transition-all duration-200 ease-out"
          :class="[
            size === 'md' ? 'max-w-md' : 'max-w-6xl',
            light ? 'bg-white' : 'bg-surface text-ink',
            {
              'translate-y-4 scale-95 opacity-0': !modelValue,
              'translate-y-0 scale-100 opacity-100': modelValue,
            },
          ]"
          role="dialog"
          aria-modal="true"
        >
          <!-- Close button -->
          <IconButton size="md" tone="filled" class="absolute top-4 right-4 z-20 hover:rotate-90" :class="light && 'dark:bg-grey-200 dark:hover:bg-grey-300'" @click="close">
            <MaterialCross color="currentColor" size="22px" />
          </IconButton>

          <!-- Modal body -->
          <div class="p-6 md:p-8">
            <slot />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'

import MaterialCross from '../svg/MaterialCross.vue'
import IconButton from '@/components/ui/IconButton.vue'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    /** 'md' for small dialogs (confirmations), 'wide' for full content. */
    size?: 'md' | 'wide'
    /** Force the white box regardless of theme — for light-only surfaces. */
    light?: boolean
  }>(),
  { size: 'wide', light: false },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const close = () => {
  emit('update:modelValue', false)
}

const handleBackdropClick = () => {
  close()
}

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.modelValue) {
    close()
  }
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = ''
    }
  },
)

onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey)
  // Ensure body scroll is restored when component is unmounted
  document.body.style.overflow = ''
})
</script>
